import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2, Square, MinusCircle, AlertTriangle, Clock, Users, FileCheck2,
  FileClock, FileX2, Percent, Search,
} from 'lucide-react';
import { WrDetailDrawer } from './WrDetailDrawer';
import { DonutCard, BarsCard, SEM } from '@/components/client/SuiteAnalytics';

const CELL_META = {
  compliant: { Icon: CheckCircle2, cls: 'text-emerald-600', label: 'status.compliant' },
  pending: { Icon: Square, cls: 'text-slate-400', label: 'status.pending' },
  na: { Icon: MinusCircle, cls: 'text-slate-300', label: 'status.na' },
  expired: { Icon: AlertTriangle, cls: 'text-red-600', label: 'status.expired' },
};

const FILE_BADGE = {
  complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  incomplete: 'bg-amber-50 text-amber-700 border-amber-200',
  expiring_soon: 'bg-orange-50 text-orange-700 border-orange-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  none: 'bg-muted text-muted-foreground',
};

const Kpi = ({ icon: Icon, label, value, accent }) => (
  <Card className="p-5 flex items-center gap-4" data-testid={`wr-kpi-${label}`}>
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${accent || 'bg-primary/10 text-primary'}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-semibold leading-tight">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  </Card>
);

export const WrWorkspace = ({ t, data, canEdit, fetchDetail, saveRecord, onSaved,
                              docMode = 'client', docCompanyId = null, docEnabled = true }) => {
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [pos, setPos] = useState('all');
  const [fstatus, setFstatus] = useState('all');
  const [reqFilter, setReqFilter] = useState('all');
  const [cellFilter, setCellFilter] = useState('all');
  const [cell, setCell] = useState(null); // {employee, requirement}

  const { requirements = [], employees = [], kpis = {} } = data || {};

  const depts = useMemo(() => [...new Set(employees.map((e) => e.department_name).filter(Boolean))].sort(), [employees]);
  const positions = useMemo(() => [...new Set(employees.map((e) => e.position).filter(Boolean))].sort(), [employees]);

  const rows = useMemo(() => employees.filter((e) => {
    if (search && !(e.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (dept !== 'all' && e.department_name !== dept) return false;
    if (pos !== 'all' && e.position !== pos) return false;
    if (fstatus !== 'all' && e.file_status !== fstatus) return false;
    if (reqFilter !== 'all') {
      const c = e.cells?.[reqFilter];
      if (!c) return false;
      if (cellFilter !== 'all') {
        if (cellFilter === 'expiring_soon' ? !c.soon : c.s !== cellFilter) return false;
      }
    }
    return true;
  }), [employees, search, dept, pos, fstatus, reqFilter, cellFilter]);

  const b = kpis.buckets || {};
  return (
    <div className="space-y-6" data-testid="wr-workspace">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-4">
        <Kpi icon={Users} label={t('wr.kpis.totalEmployees')} value={kpis.total_employees ?? 0} />
        <Kpi icon={FileCheck2} label={t('wr.kpis.completeFiles')} value={kpis.complete_files ?? 0} accent="bg-emerald-50 text-emerald-600" />
        <Kpi icon={Square} label={t('wr.kpis.withPending')} value={kpis.employees_with_pending ?? 0} accent="bg-slate-100 text-slate-600" />
        <Kpi icon={FileClock} label={t('wr.kpis.expiringSoon')} value={kpis.expiring_soon ?? 0} accent="bg-orange-50 text-orange-600" />
        <Kpi icon={FileX2} label={t('wr.kpis.expired')} value={kpis.expired ?? 0} accent="bg-red-50 text-red-600" />
        <Kpi icon={Percent} label={t('wr.kpis.avgCompletion')} value={kpis.avg_completion != null ? `${kpis.avg_completion}%` : '—'} />
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-muted-foreground" data-testid="wr-secondary-metrics">
        <span>{t('wr.kpis.globalCounts', { c: kpis.global_compliant ?? 0, a: kpis.global_applicable ?? 0 })}</span>
        <span>{t('wr.kpis.bucketExpired')}: <b className="text-foreground">{b.expired ?? 0}</b></span>
        <span>0–30: <b className="text-foreground">{b.d0_30 ?? 0}</b></span>
        <span>31–60: <b className="text-foreground">{b.d31_60 ?? 0}</b></span>
        <span>61–90: <b className="text-foreground">{b.d61_90 ?? 0}</b></span>
        <span>{t('wr.kpis.threshold', { d: data?.expiring_soon_days ?? 30 })}</span>
      </div>

      {/* Founder UAT 2026-06 — executive visual analytics (real data only) */}
      {(employees || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="wr-analytics-row">
          <DonutCard
            title={t('wr.analytics.filesTitle', { defaultValue: 'Expedientes por estado' })}
            testid="wr-chart-files"
            accent={SEM.blue}
            data={[
              { name: t('wr.analytics.complete', { defaultValue: 'Completos' }), value: employees.filter((e) => e.file_status === 'complete').length, color: SEM.green },
              { name: t('wr.analytics.expiringSoon', { defaultValue: 'Por vencer' }), value: employees.filter((e) => e.file_status === 'expiring_soon').length, color: SEM.orange },
              { name: t('wr.analytics.expired', { defaultValue: 'Vencidos' }), value: employees.filter((e) => e.file_status === 'expired').length, color: SEM.red },
              { name: t('wr.analytics.incomplete', { defaultValue: 'Incompletos' }), value: employees.filter((e) => e.file_status === 'incomplete').length, color: SEM.yellow },
              { name: t('wr.analytics.none', { defaultValue: 'Sin registros' }), value: employees.filter((e) => e.file_status === 'none').length, color: SEM.slate },
            ]}
            emptyText={t('wr.analytics.empty', { defaultValue: 'Sin expedientes todavía.' })}
          />
          <BarsCard
            title={t('wr.analytics.expiryTitle', { defaultValue: 'Vencimientos próximos' })}
            subtitle={t('wr.analytics.expirySubtitle', { defaultValue: 'Documentos vencidos y por vencer (días)' })}
            testid="wr-chart-expiry"
            accent={SEM.red}
            data={[
              { name: t('wr.kpis.bucketExpired', { defaultValue: 'Vencidos' }), value: b.expired ?? 0, color: SEM.red },
              { name: '0–30', value: b.d0_30 ?? 0, color: SEM.orange },
              { name: '31–60', value: b.d31_60 ?? 0, color: SEM.yellow },
              { name: '61–90', value: b.d61_90 ?? 0, color: SEM.blue },
            ]}
            emptyText={t('wr.analytics.noExpiry', { defaultValue: 'No hay documentos vencidos ni por vencer en los próximos 90 días.' })}
          />
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <div className="relative xl:col-span-2">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 md:text-base" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder={t('wr.filters.search')} data-testid="wr-filter-search" />
          </div>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="wr-filter-dept"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('wr.filters.allDepartments')}</SelectItem>
              {depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={pos} onValueChange={setPos}>
            <SelectTrigger data-testid="wr-filter-pos"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('wr.filters.allPositions')}</SelectItem>
              {positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fstatus} onValueChange={setFstatus}>
            <SelectTrigger data-testid="wr-filter-fstatus"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('wr.filters.allFileStatuses')}</SelectItem>
              {['complete', 'incomplete', 'expiring_soon', 'expired'].map((s) => (
                <SelectItem key={s} value={s}>{t(`wr.fileStatus.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2 xl:col-span-1">
            <Select value={reqFilter} onValueChange={(v) => { setReqFilter(v); if (v === 'all') setCellFilter('all'); }}>
              <SelectTrigger data-testid="wr-filter-req"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('wr.filters.allRequirements')}</SelectItem>
                {requirements.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {reqFilter !== 'all' && (
              <Select value={cellFilter} onValueChange={setCellFilter}>
                <SelectTrigger className="w-40" data-testid="wr-filter-cell"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('wr.filters.anyStatus')}</SelectItem>
                  {['compliant', 'pending', 'na', 'expired', 'expiring_soon'].map((s) => (
                    <SelectItem key={s} value={s}>{t(`wr.status.${s === 'expiring_soon' ? 'expiringSoon' : s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </Card>

      {/* Matrix */}
      <Card className="p-0 overflow-hidden" data-testid="wr-matrix-card">
        {requirements.length === 0 ? (
          <p className="p-10 text-base text-muted-foreground text-center" data-testid="wr-no-requirements">
            {t('wr.matrix.noRequirements')}
          </p>
        ) : (
          // Founder UAT (N) — frozen-suite PRESENTATION exception: capped height keeps the horizontal scrollbar reachable. No functional change.
          <div className="overflow-auto max-h-[72vh]" data-testid="wr-matrix-scroll">
            <table className="border-collapse min-w-full">
              <thead>
                <tr className="border-b bg-muted/40">
                  {/* A11 (limited Phase 2) — requirement headings stay visible
                      while scrolling inside the matrix; the first cell wins the
                      sticky intersection (left+top, highest z). */}
                  <th className="sticky left-0 top-0 z-40 bg-white border-r border-b text-left px-5 py-3.5 w-[350px] min-w-[350px]">
                    <div className="w-[310px]">
                      <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('wr.matrix.employee')}</span>
                    </div>
                  </th>
                  {requirements.map((r) => (
                    <th key={r.id} className="sticky top-0 z-30 bg-white border-b px-3 py-3.5 min-w-[150px] align-bottom">
                      <span className="text-sm font-medium leading-snug block text-center whitespace-normal" title={r.description || r.name}>
                        {r.name}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={requirements.length + 1} className="p-10 text-center text-base text-muted-foreground" data-testid="wr-matrix-empty">{t('wr.matrix.empty')}</td></tr>
                )}
                {rows.map((e) => (
                  <tr key={e.id} className="border-b hover:bg-muted/20" data-testid={`wr-row-${e.id}`}>
                    <td className="sticky left-0 z-10 bg-white border-r px-5 py-3">
                      <div className="flex items-center justify-between gap-3 w-[310px]">
                        <div className="min-w-0">
                          <p className="text-base font-medium truncate" title={e.name}>{e.name}</p>
                          <p className="text-sm text-muted-foreground leading-snug line-clamp-2"
                             title={[e.department_name, e.position].filter(Boolean).join(' · ') || undefined}>
                            {[e.department_name, e.position].filter(Boolean).join(' · ') || '—'}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-base font-semibold">{e.completion != null ? `${e.completion}%` : '—'}</p>
                          <Badge variant="outline" className={`text-xs ${FILE_BADGE[e.file_status]}`} data-testid={`wr-file-status-${e.id}`}>
                            {t(`wr.fileStatus.${e.file_status}`)}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    {requirements.map((r) => {
                      const c = e.cells?.[r.id];
                      if (!c) return <td key={r.id} className="px-3 py-3 text-center"><span className="text-sm text-muted-foreground/50">—</span></td>;
                      const m = CELL_META[c.s] || CELL_META.pending;
                      return (
                        <td key={r.id} className="px-3 py-3 text-center">
                          <button type="button" onClick={() => setCell({ employee: e, requirement: r })}
                                  className="inline-flex flex-col items-center gap-0.5 rounded-lg px-2 py-1 hover:bg-muted transition-colors"
                                  data-testid={`wr-cell-${e.id}-${r.id}`}>
                            <span className="flex items-center gap-1.5">
                              <m.Icon className={`h-5 w-5 ${m.cls}`} />
                              {c.soon && <Clock className="h-4 w-4 text-orange-500" title={t('wr.status.expiringSoon')} />}
                            </span>
                            <span className="text-xs text-muted-foreground">{t(`wr.${m.label}`)}</span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <WrDetailDrawer t={t} cell={cell} canEdit={canEdit} onClose={() => setCell(null)}
                      fetchDetail={fetchDetail} saveRecord={saveRecord}
                      docMode={docMode} docCompanyId={docCompanyId} docEnabled={docEnabled}
                      onSaved={() => { onSaved?.(); }} />
    </div>
  );
};
